import { Link } from "react-router-dom";
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@sarva/ui";
import type { IconName } from "@sarva/ui";
import { endSession } from "@/features/auth";
import { signOut, useSessionState } from "@/shared/api";
import { useSession } from "@/features/auth";
import { DensityMenu } from "./DensityMenu";

/**
 * doc 40 §8: `56px` tall, `bg-default`, bottom `border-default`, with
 * right-aligned icon actions and an avatar.
 *
 * There is no logo here — it lives in the sidebar's brand band, which is the
 * same height, so the two form one continuous rule across the top of the app.
 *
 * The account menu answers the three questions a person signing in to a
 * practice actually has: who am I signed in as, which practice am I looking at,
 * and how do I leave. It does **not** show a name, because `GET /me` does not
 * carry one — the email is the string the person typed at sign-in.
 *
 * (H) It used to also print the actor's UUID, the actor kind, the resolved PHI
 * tier, a count of resolved permissions and the token's expiry. Every one of
 * those is a debugging aid that reached a customer-facing surface: a biller has
 * no use for `01a0d51f-cbc8-…`, cannot act on "64 resolved", and reads a
 * countdown to an expiring token as a warning that something is wrong. Worse,
 * a raw actor id and a PHI tier are internal facts about the authorisation
 * model, and the chrome is the last place they belong. If they are wanted back
 * for support, they belong behind a deliberate diagnostics surface, not in the
 * menu every user opens to sign out.
 */

interface Action {
  icon: IconName;
  label: string;
  onSelect: () => void;
}

export interface TopNavProps {
  onOpenSearch: () => void;
  onOpenShortcuts: () => void;
  /** Rendered on the left: the tenant, the practice switcher, a filter summary. */
  leading?: React.ReactNode;
}

export function TopNav({
  onOpenSearch,
  onOpenShortcuts,
  leading,
}: TopNavProps) {
  const { me } = useSession();
  const { credentials } = useSessionState();

  // (H) Every action here does something. Four used to not.
  //
  // `robot` (Agent activity), `help`, `bell` (Notifications) and `cog` (Settings)
  // were rendered with no `onSelect` at all — six buttons in the chrome of which
  // four did nothing when clicked. Two of them had somewhere real to go and two
  // did not, and the resolution differs:
  //
  // * **Agent activity** and **Settings** are already in the rail, as `/agents`
  //   and `/practices`. A second, silent entry point to a destination that has a
  //   working one is worse than no entry point — it teaches that the chrome is
  //   decorative.
  // * **Help** and **Notifications** have nothing behind them. There is no help
  //   surface and no notifications endpoint in the contract.
  //
  // So the row is what works: search, the shortcut sheet, and density beside
  // them. Anything added back here arrives with its handler.
  const actions: Action[] = [
    { icon: "search", label: "Search (⌘K)", onSelect: onOpenSearch },
    {
      icon: "keyboard",
      label: "Keyboard shortcuts (?)",
      onSelect: onOpenShortcuts,
    },
  ];

  return (
    <header
      data-topnav="true"
      className="z-topnav flex h-topnav shrink-0 items-center gap-4 border-b border-default bg-default px-6"
    >
      <div className="min-w-0 flex-1">{leading}</div>

      <div data-topnav-actions="true" className="flex items-center gap-1">
        {/* Row density (doc 40 §10). Its own component rather than an `Action` entry,
            because it owns a popover and a selected value rather than firing a callback —
            and because until now the preference the spec promises was unreachable
            (docs/30-design/42_DESIGN_AUDIT.md, D7). */}
        <DensityMenu />
        {actions.map((action) => (
          <Tooltip key={action.label} content={action.label} side="bottom">
            <span className="relative inline-flex">
              <Button
                // (H) doc 40 §1 habit 4: neutral chrome, not brand accent.
                variant="ghost"
                size="md"
                iconOnly
                aria-label={action.label}
                onClick={action.onSelect}
              >
                <Icon name={action.icon} size={4} />
              </Button>
            </span>
          </Tooltip>
        ))}

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className={[
                "ml-1 flex h-8 w-8 items-center justify-center rounded-full",
                "bg-primary-weak text-xs font-medium text-primary",
                "outline-none focus-visible:outline focus-visible:outline-2",
                "focus-visible:outline-offset-2 focus-visible:outline-strong",
              ].join(" ")}
            >
              {/* /me carries no name and the email is only known in the tab that
                  signed in, so after a reload there is nothing to take initials
                  from — a person glyph, not a "?" that reads as an error. */}
              {credentials?.email ? initials(credentials.email) : <Icon name="users" size={4} />}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-0">
            <div className="px-3 py-2">
              <p className="truncate text-sm font-medium text-default">
                {credentials?.email ?? "Signed in"}
              </p>
              <p className="truncate text-xs text-tertiary">
                {me.tenant.display_name}
                {me.active_practice === null || me.active_practice === undefined
                  ? me.is_tenant_wide
                    ? " · all practices"
                    : ""
                  : ` · ${me.active_practice.display_name}`}
              </p>
              {/* (backend D148) A session opened from a partner product holds the user's
                  permissions minus a fixed deny-list, so a control they normally see can be
                  missing. Saying where the session came from is what makes that legible. */}
              {me.launch === null || me.launch === undefined ? null : (
                <p className="mt-1 text-xs text-secondary">
                  Opened from {me.launch.issuer_display_name}. Payments, refunds
                  and account changes need a normal sign-in.
                </p>
              )}
            </div>
            <div className="border-t border-default" />
            <ul className="py-1">
              <li>
                <Link
                  to="/security"
                  className={[
                    "flex h-8 w-full items-center gap-2 px-3 text-sm text-default",
                    "hover:bg-hover",
                    "transition-colors duration-hover ease-hover motion-reduce:transition-none",
                    "outline-none focus-visible:bg-hover",
                  ].join(" ")}
                >
                  <Icon name="shield" size={4} className="text-tertiary" />
                  Your account
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  // (H) The server session ends too, not just this tab's memory.
                  // Dropping the in-memory token alone leaves a session id in an
                  // `HttpOnly` cookie that would refresh straight back into a new
                  // token on the next reload — which on a shared machine means the
                  // "Sign out" button did not sign anyone out. `endSession` never
                  // rejects, so the local clear below always runs.
                  onClick={() => {
                    void endSession(me.tenant.id);
                    signOut("user");
                  }}
                  className={[
                    "flex h-8 w-full items-center gap-2 px-3 text-sm text-default",
                    "hover:bg-hover",
                    "transition-colors duration-hover ease-hover motion-reduce:transition-none",
                    "outline-none focus-visible:bg-hover",
                  ].join(" ")}
                >
                  <Icon name="logout" size={4} className="text-tertiary" />
                  Sign out
                </button>
              </li>
            </ul>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}

/** First letters of up to two words. `a.biller@x` -> `AB`. */
function initials(identity: string): string {
  const local = identity.split("@")[0] ?? "";
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.map((part) => part[0] ?? "").join("");
  return (letters.length > 0 ? letters : "?").slice(0, 2).toUpperCase();
}
